import _ from 'lodash'
import { Fragment, useEffect, useMemo } from 'react'
import { ThemeProvider, Theme, createTheme } from '@mui/material/styles'

import Container from '@mui/material/Container'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Unstable_Grid2'
import Stack from '@mui/material/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'

import { useGetLicensesQuery } from '@/core/services/license'

import AgendaPage from '@/pages/agenda/AgendaPage'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { getEstablishment } from '@/redux/reducers/establishment.reducer'
import { listWaitingRooms } from '@/redux/reducers/waiting-room.reducer'

import { DashboardDefaultLayout } from '@/utils/subscription/constant'
import { DashboardItemList, PartialDashboard } from '@/utils/subscription/types'
import { isStaff } from '@/utils/type-guards'

import ConsultationsCard from './containers/ConsultationsCard'
import ConsultationStatisticContainer from './containers/ConsultationStatistic'
import ExamStatisticContainer from './containers/ExamsStatistic'
import ExpertiseAndConsultationsTab from './containers/ExpertisesAndConsultationsTabs'
import ExpertisesCardContainer from './containers/ExpertisesCard'
import LocalConsultationCardContainer from './containers/LocalConsultationsCard'
import MeetingLinksStatistic from './containers/MeetingLinksStatistic'
import PatientsCardContainer from './containers/PatientsCard'
import SelectDeviceCard from './containers/SelectDevice'
import FavoriteWaitingRoomsCard from './containers/FavoriteWaitingRoomsCard'
import WaitingRoomCard from './containers/WaitingRoomCard'
import WelcomeCard from './containers/WelcomeCard'
import ShiftDashboardCardContainer from './containers/ShiftDashboardCard'
import { EmergencyQuickAccess } from './components/EmergencyQuickAccess'
import { useServiceWorker } from '@/hooks/useServiceWorker'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useAbility } from '@/providers/AbilityProvider'
import { FeatureFlag, useIsFlagEnabled } from '@/hooks/useIsFlagEnabled'
import { DashboardOmnibox } from '@/features/dashboard-omnibox'
import { DashboardCard, groupIntoBands } from './lib/dashboard-bands'

const hideConfigAppButton = () => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage('connected')

    const originalLog = console.log
    console.log = (...args) => {
      window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'log', data: args }))
      originalLog(...args)
    }

    const errorLog = console.error
    console.error = (...args) => {
      window.ReactNativeWebView?.postMessage(JSON.stringify({ type: 'error', data: args }))
      errorLog(...args)
    }
  }
}

const homepageThemeBuilder = (theme: Theme) =>
  createTheme(theme, {
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: '16px',
            boxShadow: 'rgb(145 158 171 / 20%) 0px 0px 2px 0px, rgb(145 158 171 / 12%) 0px 12px 24px -4px',
          },
          title: {
            color: '#35374C',
            fontWeight: 700,
            fontSize: '20px',
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: '24px 24px 16px 24px',
          },
          title: {
            color: '#35374C',
            fontWeight: 700,
            fontSize: '20px',
          },
        },
      },
    },
  })

function Agenda() {
  const hour = new Date().getHours()
  return <AgendaPage cellDuration={90} disableDrawer disableSchedules disableViewSwitcher fullScreen={false} startDayHour={hour - 2} endDayHour={hour + 6} scrollToNow={false} />
}

export const dashboardItemList: DashboardItemList = [
  {
    label: PartialDashboard.EmergencyQuickAccess,
    component: <EmergencyQuickAccess />,
    order: 3,
  },
  {
    label: PartialDashboard.FavoriteWaitingRooms,
    component: <FavoriteWaitingRoomsCard />,
    order: 9,
  },
  {
    label: PartialDashboard.WaitingRoom,
    component: <WaitingRoomCard />,
    order: 10,
  },
  {
    label: PartialDashboard.Consultations,
    component: <ConsultationsCard />,
    order: 7,
  },
  {
    label: PartialDashboard.Patients,
    component: <PatientsCardContainer />,
    order: 4,
  },
  {
    label: PartialDashboard.Expertises,
    component: <ExpertisesCardContainer />,
    order: 13,
  },
  {
    label: PartialDashboard.Welcome,
    component: <WelcomeCard />,
    order: 1,
  },
  {
    label: PartialDashboard.ConsultationsStatistics,
    component: <ConsultationStatisticContainer />,
    order: 2,
  },
  {
    label: PartialDashboard.ExpertiseAndConsultationsTab,
    component: <ExpertiseAndConsultationsTab />,
    order: 5,
  },
  {
    label: PartialDashboard.LocalConsultation,
    component: <LocalConsultationCardContainer />,
    order: 10,
  },
  {
    label: PartialDashboard.Equipments,
    component: <SelectDeviceCard />,
    order: 11,
  },
  {
    label: PartialDashboard.ShiftDashboard,
    component: <ShiftDashboardCardContainer />,
    order: 6,
  },
  {
    label: PartialDashboard.ExamsStatistics,
    component: <ExamStatisticContainer />,
    order: 12,
  },
  {
    label: PartialDashboard.MeetingLinksStatistics,
    component: <MeetingLinksStatistic />,
    order: 8,
  },
  {
    label: PartialDashboard.Agenda,
    component: (
      <Card>
        <Agenda />
      </Card>
    ),
    order: 14,
  },
]

type HomeCards = DashboardCard<JSX.Element>[]

/** Below md: one grid, cards flow in order (each row as tall as its tallest card). */
function FlatDashboard({ cards }: { cards: HomeCards }) {
  return (
    <>
      {cards.map((card) => (
        <Grid {...card.config.col} key={card.id}>
          {card.component}
        </Grid>
      ))}
    </>
  )
}

/** md and up: wide and narrow cards stack in two independent columns, so a short card leaves no hole. */
function BandedDashboard({ cards }: { cards: HomeCards }) {
  return (
    <>
      {groupIntoBands(cards).map((band) =>
        band.kind === 'full' ? (
          <Grid xs={12} key={band.card.id}>
            {band.card.component}
          </Grid>
        ) : (
          <Fragment key={[...band.main, ...band.side].map((card) => card.id).join('|')}>
            {band.main.length > 0 && (
              <Grid md={band.side.length > 0 ? 8 : 12}>
                <Stack spacing={3}>{band.main.map((card) => <div key={card.id}>{card.component}</div>)}</Stack>
              </Grid>
            )}
            {band.side.length > 0 && (
              <Grid md={4}>
                <Stack spacing={3}>{band.side.map((card) => <div key={card.id}>{card.component}</div>)}</Stack>
              </Grid>
            )}
          </Fragment>
        )
      )}
    </>
  )
}

export default function HomePage() {
  const dispatch = useAppDispatch()
  const ability = useAbility()
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const isOmniboxEnabled = useIsFlagEnabled(FeatureFlag.DashboardElaraOmnibox)

  const authUser = useAppSelector(selectAuthUser)

  const { data: licenses } = useGetLicensesQuery(
    { id: isStaff(authUser) ? authUser.licenses : undefined },
    { skip: !isStaff(authUser) || !authUser.licenses?.length }
  )

  useServiceWorker()

  const homePageItems = useMemo(() => {
    const allowedDashboardComponents = _.uniq(_.flatten(licenses?.map((license) => license.components)))
      .filter((component) => component.startsWith('dashboard:'))
      .map((component) => component.replace('dashboard:', '')) as PartialDashboard[]
    return _.sortBy(dashboardItemList, 'order')
      .filter((item) => allowedDashboardComponents.includes(item.label))
      .filter((item) => {
        if ([PartialDashboard.ConsultationsStatistics, PartialDashboard.ExamsStatistics].includes(item.label)) {
          return ability.can('read', 'Statistics')
        }
        return true
      })
      .map((item) => ({ id: item.label, config: DashboardDefaultLayout[item.label], component: item.component }))
  }, [ability, licenses])

  useEffect(() => hideConfigAppButton(), [])

  useEffect(() => {
    if (!authUser) return

    if (isStaff(authUser) && authUser.status === 'PDS') dispatch(listWaitingRooms({ isPrioritized: true }))

    if (!!authUser.establishment && typeof authUser.establishment === 'string') {
      dispatch(getEstablishment(authUser.establishment))
    }
  }, [dispatch, authUser])

  usePageTitle('Dashboard')

  if (!authUser) return null

  return (
    <ThemeProvider theme={homepageThemeBuilder}>
      <Container maxWidth="xl" sx={{ p: '24px' }} id="home">
        <h1 className="sr-only">Dashboard</h1>
        <Grid container spacing={3} sx={{ height: '100%' }}>
          {isOmniboxEnabled && (
            <Grid xs={12}>
              <DashboardOmnibox />
            </Grid>
          )}
          {isDesktop ? <BandedDashboard cards={homePageItems} /> : <FlatDashboard cards={homePageItems} />}
        </Grid>
      </Container>
    </ThemeProvider>
  )
}
